import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { STAFF_ROLES } from '../config/features';
import useFeatureBadges from '../hooks/useFeatureBadges';
import { bagCount } from './bag';
import { useBagLines } from './useBagLines';
import { useWishlist } from './savedLists';
import { useCategories, useTopBrands } from './catalogApi';
import BagDrawer from './BagDrawer';
import QuickView from './QuickView';
import Sheet from './Sheet';
import SearchBox from './SearchBox';
import MegaMenu, { BrandColumns, BrandTiles, CategoryColumns } from './MegaMenu';
import { ChevronIcon, BagIcon, HeartIcon, MenuIcon, SearchIcon, UserIcon } from './Icons';
import { OPEN_BAG, OPEN_QUICK_VIEW, openBag } from './ui';
import { CORPORATE, STORE } from './storeConfig';
import { categoryLink, formatInr } from './retailUtils';
import './storefront.css';

// The Brands menu lists every brand (A–Z text) under a row of the most popular logos. Same
// request as the /brands page (ALL_BRANDS), so it's fetched once.
const ALL_BRANDS = 500;
const MENU_LOGOS = 8;

/** Which header entry the current listing belongs to: New arrivals and Deals are listings in a
 *  given order, Brands covers /brands and any one-brand listing, Categories the rest of /catalog. */
function headerSection({ pathname, search }) {
  if (pathname === '/brands') return 'brands';
  if (pathname !== '/catalog') return null;
  const p = new URLSearchParams(search);
  if (p.get('sort') === 'discount') return 'deals';
  if (p.get('sort') === 'newest') return 'new';
  if (p.get('brand')) return 'brands';
  return 'categories';
}
// Pages that get the phone bag bar (not the product page, whose own buttons are pinned there).
const BAG_BAR_PAGES = ['/', '/catalog', '/search', '/wishlist'];

/** Thin black bar above the store header for signed-in eYantra staff: their tools stay one
 *  click away without turning the shop into a back office. */
function StaffBar({ role }) {
  const badges = useFeatureBadges(role);
  const waiting = (badges.pending || 0) + (badges.brandstore || 0);
  const links = [
    { to: '/dashboard', label: 'Workspace', count: waiting },
    { to: '/admin?tab=carts', label: 'Curated carts' },
    ...(role === 'ADMIN' || role === 'CURATOR' ? [{ to: '/admin', label: 'Admin' }] : []),
  ];
  return (
    <div className="sf-staffbar" role="navigation" aria-label="eYantra staff">
      <div className="sf-staffbar-inner">
        <span className="sf-staffbar-label">eYantra staff</span>
        <div className="sf-staffbar-links">
          {links.map(l => (
            <NavLink key={l.to} to={l.to} end>
              {l.label}
              {l.count > 0 && <span className="sf-staffbar-count">{l.count > 99 ? '99+' : l.count}<span className="rt-sr-only"> waiting</span></span>}
            </NavLink>
          ))}
        </div>
      </div>
    </div>
  );
}

/** "Account" with a small menu for signed-in users; "Log in" otherwise. */
function AccountMenu({ user, onSignOut }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const location = useLocation();
  useEffect(() => { setOpen(false); }, [location.pathname, location.search]);
  useEffect(() => {
    if (!open) return undefined;
    const onDown = e => { if (!ref.current?.contains(e.target)) setOpen(false); };
    const onKey = e => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    window.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); window.removeEventListener('keydown', onKey); };
  }, [open]);

  if (!user) return <Link to="/login" className="rt-text-link rt-only-desktop">Log in</Link>;
  const isStaff = STAFF_ROLES.includes(user.role);
  return (
    <div className="sf-account rt-only-desktop" ref={ref}>
      <button type="button" className="rt-text-link" aria-haspopup="menu" aria-expanded={open} onClick={() => setOpen(o => !o)}>Account</button>
      {open && (
        <div className="sf-account-menu" role="menu">
          <div className="sf-account-who">
            <strong>{user.username}</strong>
            <span>{user.role?.replace(/_/g, ' ').toLowerCase()}</span>
          </div>
          {isStaff
            ? <Link role="menuitem" to="/dashboard">Workspace</Link>
            : <Link role="menuitem" to="/my-carts">My curated carts</Link>}
          <Link role="menuitem" to="/quote">My quote</Link>
          <Link role="menuitem" to="/wishlist">Wishlist</Link>
          <button type="button" role="menuitem" onClick={onSignOut}>Sign out</button>
        </div>
      )}
    </div>
  );
}

/** The storefront chrome — the retail store's header, footer, bag drawer and quick view.
 *  `wide` lets the page run edge to edge (banners, rails); `narrow` is for forms. */
export default function StoreLayout({ children, narrow = false, wide = false, portal = false }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const categories = useCategories();
  const brands = useTopBrands(ALL_BRANDS);
  const section = headerSection(location);
  const { bag, subtotal } = useBagLines();
  const count = bagCount(bag);
  const saved = useWishlist().length;
  const [bagOpen, setBagOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [quickProduct, setQuickProduct] = useState(null);
  const isStaff = STAFF_ROLES.includes(user?.role);
  const signOut = () => { logout(); navigate('/'); };

  useEffect(() => {
    const onBag = () => setBagOpen(true);
    const onQuick = (e) => setQuickProduct(e.detail);
    window.addEventListener(OPEN_BAG, onBag);
    window.addEventListener(OPEN_QUICK_VIEW, onQuick);
    return () => { window.removeEventListener(OPEN_BAG, onBag); window.removeEventListener(OPEN_QUICK_VIEW, onQuick); };
  }, []);
  useEffect(() => { setBagOpen(false); setQuickProduct(null); setMenuOpen(false); setSearchOpen(false); }, [location.pathname, location.search]);
  const closeBag = useCallback(() => setBagOpen(false), []);
  // Phones: once something is in the bag, a bar pinned to the bottom keeps it one tap away.
  const showBagBar = count > 0 && BAG_BAR_PAGES.includes(location.pathname);

  return (
    <div className={`rt-page${showBagBar ? ' rt-page--bagbar' : ''}${portal ? ' rt-page--portal' : ''}`}>
      <a href="#rt-main" className="rt-skip" onClick={e => { e.preventDefault(); document.getElementById('rt-main')?.focus(); }}>Skip to content</a>
      {isStaff && <StaffBar role={user.role} />}
      <header className="rt-header">
        <div className="rt-header-inner">
          <div className="rt-header-left">
            <button type="button" className="rt-icon-btn rt-only-mobile" aria-label="Menu" onClick={() => setMenuOpen(true)}><MenuIcon /></button>
            <nav className="rt-nav rt-only-desktop" aria-label="Shop">
              {/* Four fixed entries that always fit; the menus list every category and the top brands. */}
              <MegaMenu label="Categories" to="/catalog" active={section === 'categories'} panelLabel="All categories"
                foot={<Link to="/catalog">View all products</Link>}>
                {categories.length > 0 && <CategoryColumns categories={categories} />}
              </MegaMenu>
              <MegaMenu label="Brands" to="/brands" active={section === 'brands'} panelLabel="All brands"
                foot={<Link to="/brands">Browse brands A–Z</Link>}>
                {brands.length > 0 && <>
                  <BrandTiles brands={brands.slice(0, MENU_LOGOS)} compact />
                  <BrandColumns brands={brands} />
                </>}
              </MegaMenu>
              <Link to="/catalog?sort=newest" className={section === 'new' ? 'active' : undefined}>New arrivals</Link>
              <Link to="/catalog?sort=discount" className={section === 'deals' ? 'active' : undefined}>Deals</Link>
            </nav>
          </div>
          <Link to="/" className="rt-brand sf-brand" aria-label="eYantra home">eYantra</Link>
          <div className="rt-header-right">
            <Link to={CORPORATE.path} className="rt-text-link rt-only-desktop">{CORPORATE.navLabel}</Link>
            <button type="button" className="rt-text-link rt-only-desktop" onClick={() => setSearchOpen(true)}>Search</button>
            <AccountMenu user={user} onSignOut={signOut} />
            <Link to="/wishlist" className="rt-text-link rt-only-desktop">Wishlist{saved ? ` (${saved})` : ''}</Link>
            <button type="button" className="rt-text-link rt-only-desktop" onClick={openBag}>Bag ({count})</button>
            <button type="button" className="rt-icon-btn rt-only-mobile" aria-label="Search" onClick={() => setSearchOpen(true)}><SearchIcon /></button>
            <button type="button" className="rt-icon-btn rt-only-mobile" aria-label={`Bag, ${count} item${count !== 1 ? 's' : ''}`} onClick={openBag}>
              <BagIcon />{count > 0 && <span className="rt-badge-dot">{count > 99 ? '99+' : count}</span>}
            </button>
          </div>
        </div>
      </header>

      <main id="rt-main" tabIndex={-1} className={`rt-main${narrow ? ' rt-main--narrow' : ''}${wide ? ' rt-main--wide' : ''}`}>{children}</main>

      <footer className="rt-footer">
        <div className="rt-footer-inner">
          <div>
            <div className="rt-footer-brand">eYantra</div>
            <p className="rt-footer-note">
              {STORE.tagline}. Prices include GST. Offices in Hyderabad, Noida, Mumbai and Bengaluru.
            </p>
          </div>
          <div>
            <h4>Shop</h4>
            <Link to="/catalog">All products</Link>
            <Link to="/brands">Brands</Link>
            <Link to="/catalog?sort=discount">Deals</Link>
            {categories.slice(0, 4).map(c => <Link key={c.name} to={categoryLink(c.name)}>{c.name}</Link>)}
          </div>
          <div>
            <h4>For business</h4>
            <Link to={CORPORATE.path}>{CORPORATE.linkLabel}</Link>
            <Link to="/gallery">Our work</Link>
            <Link to="/quote">My quote</Link>
          </div>
          <div>
            <h4>Help</h4>
            <Link to="/wishlist">Wishlist</Link>
            <Link to="/privacy-policy">Privacy policy</Link>
            <Link to="/terms">Terms of service</Link>
          </div>
        </div>
        <div className="rt-footer-bottom">
          <span>© {new Date().getFullYear()} eYantra</span>
          <span>{STORE.tagline}</span>
        </div>
      </footer>

      <Sheet open={menuOpen} onClose={() => setMenuOpen(false)} title="Menu" side="left">
        <nav className="rt-menu" aria-label="Shop">
          <Link to="/">Home</Link>
          <Link to="/catalog?sort=newest">New arrivals</Link>
          <Link to="/catalog?sort=discount">Deals</Link>
          <Link to="/brands">Brands</Link>
          {/* Near the top: below the category list, companies would never find it. */}
          <Link to={CORPORATE.path}>{CORPORATE.navLabel}</Link>
          {/* Every category, folded away: one "Categories" row that opens to the categories, each
              opening to its types — instead of a flat list of 20-odd categories. */}
          <details className="sf-menu-group">
            <summary>Categories <ChevronIcon size={14} /></summary>
            <div className="sf-menu-sub">
              <Link to="/catalog">All products</Link>
              {categories.map(c => (c.subcategories?.length > 1 ? (
                <details key={c.name} className="sf-menu-group sf-menu-group--inner">
                  <summary>{c.name} <ChevronIcon size={13} /></summary>
                  <div className="sf-menu-sub">
                    <Link to={categoryLink(c.name)}>All {c.name}</Link>
                    {c.subcategories.map(s => <Link key={s} to={categoryLink(c.name, s)}>{s}</Link>)}
                  </div>
                </details>
              ) : <Link key={c.name} to={categoryLink(c.name)}>{c.name}</Link>))}
            </div>
          </details>
        </nav>
        <nav className="rt-menu rt-menu--secondary" aria-label="Account">
          {user
            ? <button type="button" className="sf-menu-btn" onClick={signOut}><UserIcon size={18} /> Sign out ({user.username})</button>
            : <Link to="/login"><UserIcon size={18} /> Log in</Link>}
          {isStaff && <Link to="/dashboard">Workspace</Link>}
          <Link to="/wishlist"><HeartIcon size={18} /> Wishlist{saved ? ` (${saved})` : ''}</Link>
          <Link to="/quote">My quote</Link>
        </nav>
      </Sheet>

      <Sheet open={searchOpen} onClose={() => setSearchOpen(false)} title="Search" side="top">
        <SearchBox autoFocus onDone={() => setSearchOpen(false)} />
        {categories.length > 0 && (
          <div className="rt-search-popular">
            <div className="rt-eyebrow">Popular</div>
            <div className="rt-pills" style={{ flexWrap: 'wrap' }}>
              {categories.slice(0, 10).map(c => <Link key={c.name} to={categoryLink(c.name)} className="rt-pill">{c.name}</Link>)}
            </div>
          </div>
        )}
      </Sheet>

      {showBagBar && (
        <button type="button" className="rt-bagbar rt-only-mobile" onClick={openBag}
          aria-label={`View bag: ${count} item${count !== 1 ? 's' : ''}, ${formatInr(subtotal)}`}>
          <BagIcon size={18} />
          <span><strong>{count} item{count !== 1 ? 's' : ''}</strong> · {formatInr(subtotal)}</span>
          <span className="rt-bagbar-cta">View bag →</span>
        </button>
      )}
      <BagDrawer open={bagOpen} onClose={closeBag} />
      {quickProduct && <QuickView key={quickProduct.id} product={quickProduct} onClose={() => setQuickProduct(null)} />}
    </div>
  );
}
